// The Subscriptions dialog's state (MailingList.vue; Seán's spec, 2026-10-23). The hash says
// what the person came to do (useMailingListLink.js); the account holds what they're subscribed
// to; the dialog shows that and changes it.
// - The entry action (#subscribe: all on, #unsubscribe: all off) runs once per arrival at the
//   hash, once the lists are loaded; an email's link (by its token) too: opening it does the
//   job (Seán, 2026-10-23: no button to confirm it).
// - A change of the person's own leaves the action: the hash becomes #subscriptions (replaced,
//   no history entry), the dialog carries on as it is, and the action never runs again.
// - Signed out without a token: #sign-in, then back to the same hash.
import {computed, ref, watch} from "vue";
import {useRoute, useRouter} from "vue-router";
import useUserSafe from "@/composables/useUserSafe";
import {useAccountLists} from "@/composables/useMailingLists";
import {useSubscriptionSaves} from "@/composables/useSubscriptionSaves";
import {setSignInRequest} from "@/composables/useSignInRequest";
import {
  fetchTokenLists, getLink, getListsAfter, getOrdered, saveTokenLists,
} from "@/composables/useMailingListLink";

const VIEW_HASH = "#subscriptions";

// "loading", "none", "some" or "all": what the saved lists amount to
function getSummary(lists) {
  if (!lists) return "loading";
  if (!lists.length) return "none";
  return lists.length === 3 ? "all" : "some";
}

export function useSubscriptionsDialog() {
  const [route, router] = [useRoute(), useRouter()];
  const {isLoaded, isSignedIn} = useUserSafe();
  const account = useAccountLists();
  const entry = ref(null); // {mode, token, lists, hash, isActionDone}
  const isLinkBroken = ref(false);
  const saves = useSubscriptionSaves((lists) => {
    const token = entry.value?.token;
    return token ? saveTokenLists(token, lists) : account.persist(lists);
  });
  let ownHash = ""; // the hash this dialog set itself: not a new arrival
  const summary = computed(() => getSummary(saves.confirmed.value));

  function runAction() {
    entry.value.isActionDone = true;
    saves.request(getListsAfter(entry.value, saves.confirmed.value));
  }

  function leaveAction() {
    if (entry.value.mode === "view") return;
    Object.assign(entry.value, {mode: "view", isActionDone: true});
    ownHash = VIEW_HASH;
    router.replace({path: route.path, hash: VIEW_HASH});
  }

  function toggle(key, isOn) {
    leaveAction();
    const current = saves.desired.value.filter((item) => item !== key);
    saves.request(getOrdered(isOn ? [...current, key] : current));
  }

  async function loadToken(token) {
    const lists = await fetchTokenLists(token);
    if (entry.value?.token !== token) return; // gone on to another link meanwhile
    if (lists) return saves.load(lists);
    isLinkBroken.value = true;
  }

  function arrive(hash) {
    if (hash === ownHash) return;
    ownHash = "";
    const opened = getLink(hash);
    saves.reset();
    isLinkBroken.value = false;
    entry.value = opened ? {...opened, hash, isActionDone: opened.mode === "view"} : null;
    if (opened?.token) loadToken(opened.token);
  }

  function sendToSignIn(hash) {
    router.replace({path: route.path, hash: "#sign-in"}).then(() => {
      setSignInRequest("subscribe", () => router.push({hash})); // set once there: see SignIn
    });
  }

  watch(() => route.hash, arrive, {immediate: true});
  // the account's lists, when signed in (Clerk loaded: no one signed in is sent to #sign-in)
  watch([entry, isLoaded, isSignedIn, account.lists], ([opened, loaded, signedIn, lists]) => {
    if (!opened || opened.token || !loaded) return;
    if (!signedIn) return sendToSignIn(opened.hash);
    if (lists) saves.load(lists);
  }, {immediate: true});
  // the entry action, once the lists are known (an email's link's too)
  watch([entry, saves.confirmed], ([opened, confirmed]) => {
    if (!opened || opened.isActionDone || !confirmed) return;
    runAction();
  }, {immediate: true});

  return {entry, saves, summary, isLinkBroken, isSignedIn, toggle};
}
